import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic2, HandCoins, Users, Ticket, Store } from "lucide-react";
import SectionLabel from "@/components/site/SectionLabel";
import CardMark from "@/components/site/CardMark";
import Seo from "@/components/site/Seo";
import {
  AUDIENCE_TAGS,
  BRAND,
  ASSETS,
  PROMISES,
} from "@/lib/constants";

const REASONS = [
  {
    h: "A Real Stage",
    p: "Professional production, live audience, structured judging. Not a practice room.",
  },
  {
    h: "Meaningful Feedback",
    p: "Substantive critique from coaches, producers, and seasoned communicators.",
  },
  {
    h: "Audience Exposure",
    p: "Reach attendees, sponsors, publishers, and community leaders in one room.",
  },
  {
    h: "Your Moment, Captured",
    p: "Your talk is professionally documented in photo and video. Selected contestants may receive footage of their own performance, subject to the contestant media agreement.",
  },
  {
    h: "Opportunity Pipeline",
    p: "Connections to speaking, publishing, coaching, and business pathways.",
  },
  {
    h: "A Standard Worth Meeting",
    p: "A proving ground that respects the seriousness of your message.",
  },
];

const NOT_LIST = [
  "Not an open mic",
  "Not a speaker's bureau",
  "Not a pay-to-play vanity event",
  "Not a networking mixer pretending to be a competition",
];

const PROMISE_CARDS = [
  { tag: "To Contestants", body: PROMISES.contestants },
  { tag: "To Audiences", body: PROMISES.audiences },
  { tag: "To Sponsors & Partners", body: PROMISES.sponsors },
];

const CTA_TRIO = [
  {
    icon: Mic2,
    label: "Speak",
    to: "/apply",
    blurb: "Apply to step onto the stage.",
    testId: "trio-cta-speak",
  },
  {
    icon: HandCoins,
    label: "Sponsor",
    to: "/sponsor",
    blurb: "Align your brand with emerging voices.",
    testId: "trio-cta-sponsor",
  },
  {
    icon: Users,
    label: "Attend",
    to: "/attend",
    blurb: "Be in the room when it happens.",
    testId: "trio-cta-attend",
  },
];

export default function Home() {
  const parallaxRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "The Next Stage · Public Speaking Showcase · Colorado Springs";
  }, []);

  // Parallax: slowly translate the hero background image UP as user scrolls.
  // Respects prefers-reduced-motion, throttled to rAF.
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const hero = heroRef.current;
        const bg = parallaxRef.current;
        if (hero && bg) {
          const rect = hero.getBoundingClientRect();
          // Only animate while hero is in view (rect.bottom > 0 and rect.top < viewport)
          if (rect.bottom > 0 && rect.top < window.innerHeight) {
            // How far we've scrolled past the hero's top (in px)
            const scrolled = Math.max(0, -rect.top);
            // Translate the bg upward at 35% of scroll — slow, cinematic
            const translate = scrolled * 0.35;
            bg.style.transform = `translate3d(0, -${translate}px, 0) scale(1.12)`;
          }
        }
        ticking = false;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div>
      <Seo />
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative overflow-hidden min-h-[86vh] flex items-start"
      >
        {/* Cinematic poster background — anchored to the bottom so the
            silhouettes-walking-onto-stage moment is the dominant visual.
            Translated upward on scroll via JS for a slow parallax. */}
        <div
          ref={parallaxRef}
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-no-repeat will-change-transform"
          style={{
            backgroundImage: `url(${ASSETS.homeHeroBg})`,
            backgroundPosition: "center bottom",
            transform: "translate3d(0, 0, 0) scale(1.12)",
            top: "-10%",
            height: "120%",
          }}
        />
        {/* Global dark atmospheric scrim — keeps image readable but subdued */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(2,8,20,0.78) 0%, rgba(2,8,20,0.62) 45%, rgba(2,8,20,0.82) 100%)",
          }}
        />
        {/* Left-side text-legibility wash */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(2,8,20,0.85) 0%, rgba(2,8,20,0.55) 40%, rgba(2,8,20,0.25) 70%, rgba(2,8,20,0.55) 100%)",
          }}
        />
        {/* Warm gold spotlight kiss from top */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 35% 10%, rgba(243,198,76,0.22) 0%, transparent 65%)",
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 lg:pt-6 pb-16 lg:pb-20 relative z-10 w-full">
          <img
            src={ASSETS.brandLogoBanner}
            alt="The Next Stage — Taking Your Public Speaking Journey To The Next Stage"
            className="w-full max-h-[180px] sm:max-h-[220px] lg:max-h-[260px] h-auto object-contain animate-fade-up [filter:drop-shadow(0_10px_40px_rgba(0,0,0,0.6))]"
            loading="eager"
            data-testid="home-hero-logo"
          />
          <div className="max-w-2xl lg:max-w-3xl mt-4 lg:mt-6">
            <h1 className="font-bebas text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight whitespace-nowrap animate-fade-up stagger-1 [text-shadow:_0_4px_24px_rgba(0,0,0,0.65)]">
              Step Onto <span className="gold-text">The Stage.</span>
            </h1>
            <p className="font-cormorant text-2xl sm:text-3xl text-gold-bright/95 mt-6 max-w-2xl animate-fade-up stagger-2 [text-shadow:_0_2px_18px_rgba(0,0,0,0.7)]">
              A powerful message deserves more than a private dream. It
              deserves a stage.
            </p>
            <p className="font-manrope text-soft-gray text-base sm:text-lg mt-6 max-w-2xl leading-relaxed animate-fade-up stagger-3 [text-shadow:_0_2px_14px_rgba(0,0,0,0.7)]">
              A live public speaking competition and showcase for emerging
              voices — entrepreneurs, authors, advocates, coaches, veterans,
              and community leaders with something serious to say.
            </p>
            <div
              className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-3xl animate-fade-up stagger-4"
              data-testid="hero-cta-grid"
            >
              <Link
                to="/apply"
                data-testid="hero-cta-apply"
                className="hero-tile hero-tile--primary"
              >
                <span className="hero-tile-icon" aria-hidden="true">
                  <Mic2 size={20} strokeWidth={1.8} />
                </span>
                <span>
                  <span className="hero-tile-label block">Apply To Speak</span>
                  <span className="hero-tile-meta block mt-1.5">
                    Step onto the stage
                  </span>
                </span>
                <ArrowRight size={16} className="hero-tile-arrow" />
              </Link>

              <Link
                to="/sponsor"
                data-testid="hero-cta-sponsor"
                className="hero-tile"
              >
                <span className="hero-tile-icon" aria-hidden="true">
                  <HandCoins size={20} strokeWidth={1.8} />
                </span>
                <span>
                  <span className="hero-tile-label block">Sponsor The Show</span>
                  <span className="hero-tile-meta block mt-1.5">
                    Align your brand
                  </span>
                </span>
                <ArrowRight size={16} className="hero-tile-arrow" />
              </Link>

              <Link
                to="/contact"
                data-testid="hero-cta-vendor"
                className="hero-tile"
              >
                <span className="hero-tile-icon" aria-hidden="true">
                  <Store size={20} strokeWidth={1.8} />
                </span>
                <span>
                  <span className="hero-tile-label block">Be A Vendor</span>
                  <span className="hero-tile-meta block mt-1.5">
                    Bring your offering
                  </span>
                </span>
                <ArrowRight size={16} className="hero-tile-arrow" />
              </Link>

              <Link
                to="/attend"
                data-testid="hero-cta-attend"
                className="hero-tile"
              >
                <span className="hero-tile-icon" aria-hidden="true">
                  <Ticket size={20} strokeWidth={1.8} />
                </span>
                <span>
                  <span className="hero-tile-label block">Attend The Show</span>
                  <span className="hero-tile-meta block mt-1.5">
                    Be in the room
                  </span>
                </span>
                <ArrowRight size={16} className="hero-tile-arrow" />
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl text-xs font-bebas tracking-[0.22em] text-muted-ink uppercase animate-fade-up stagger-5">
              <div>
                <div className="text-gold text-base">{BRAND.pilots.one}</div>
                <div>{BRAND.city}</div>
              </div>
              <div>
                <div className="text-gold text-base">{BRAND.pilots.two}</div>
                <div>{BRAND.city}</div>
              </div>
              <div>
                <div className="text-gold text-base">Presented By</div>
                <div>{BRAND.producerShort}</div>
              </div>
            </div>
          </div>
        </div>
        {/* Bottom hairline to anchor section break */}
        <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent z-10" />
      </section>

      {/* WHAT IT IS */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel number="01">What It Is</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Not An Open Mic.
              <br />
              <span className="gold-text">A Real Stage.</span>
            </h2>
            <p className="font-cormorant text-2xl text-gold-bright/90 mt-6">
              The winner matters. The process matters more.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-slate-soft">
            <p className="font-manrope text-soft-gray text-base sm:text-lg leading-relaxed">
              <span className="text-white font-medium">The Next Stage</span> is a
              live, professionally produced public speaking competition and
              showcase for emerging speakers who are serious about their
              message. Structured judging. Real audiences. Substantive feedback.
              A pipeline to opportunity.
            </p>
            <p className="font-manrope text-soft-gray text-base sm:text-lg leading-relaxed mt-6">
              This is not a shortcut. It is a proving ground. A place where
              communication is treated as leadership — and where the room
              expects you to bring something true.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-3">
              {NOT_LIST.map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-3 font-manrope text-muted-ink text-sm"
                >
                  <span className="text-gold mt-1">×</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ENTRY PREVIEW */}
      <section
        data-testid="entry-preview-section"
        className="bg-midnight border-t border-slate-soft"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <SectionLabel>Entry Preview</SectionLabel>
            <p className="font-bebas tracking-[0.32em] text-muted-ink text-xs uppercase">
              Rendering shown · Arrival experience
            </p>
          </div>
          <div className="corner-bracket p-2 bg-slate-card-2">
            <img
              src={ASSETS.entryPreview}
              alt="Rendering of The Next Stage entry — welcome banner above the doorway, check-in table, branded step-and-repeat, and guests arriving into the showcase"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
          <p className="font-cormorant text-2xl sm:text-3xl text-gold-bright/90 mt-8 max-w-2xl">
            The moment you walk in.
          </p>
        </div>
      </section>

      {/* STAGE PREVIEW */}
      <section
        data-testid="stage-preview-section"
        className="bg-midnight border-t border-slate-soft"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <SectionLabel>Stage Preview</SectionLabel>
            <p className="font-bebas tracking-[0.32em] text-muted-ink text-xs uppercase">
              Rendering shown · Final venue to be announced
            </p>
          </div>
          <div className="corner-bracket p-2 bg-slate-card-2">
            <img
              src={ASSETS.stagePreview}
              alt="Rendering of The Next Stage event setup — lit stage with brand backdrop, judges' table, podium, audience seating, and event banners"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
          <p className="font-cormorant text-2xl sm:text-3xl text-gold-bright/90 mt-8 max-w-2xl">
            The room you are stepping into.
          </p>
        </div>
      </section>

      {/* WHY STEP FORWARD */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex flex-col items-start">
            <SectionLabel number="02">Why Step Forward</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
              Six Reasons This Stage <span className="gold-text">Is Worth It.</span>
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {REASONS.map((r) => (
              <div key={r.h} className="card-dark p-8 relative">
                <div className="flex items-center gap-3">
                  <CardMark />
                  <h3 className="font-shoulders font-bold uppercase tracking-wider text-2xl text-white relative z-10">
                    {r.h}
                  </h3>
                </div>
                <div className="gold-line" />
                <p className="font-manrope text-soft-gray mt-4 leading-relaxed relative z-10">
                  {r.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO SHOULD APPLY */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <SectionLabel number="03">Who Should Apply</SectionLabel>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                Emerging Voices.
                <br />
                <span className="gold-text">Serious Messages.</span>
              </h2>
              <p className="font-manrope text-soft-gray mt-6 leading-relaxed">
                You do not need to be a polished keynoter. You need to be
                serious about your message and ready to do the work.
              </p>
            </div>
            <div className="lg:col-span-7 flex flex-wrap gap-2">
              {AUDIENCE_TAGS.map((t, i) => (
                <span
                  key={t}
                  data-testid={`audience-tag-${i}`}
                  className="pill-chip"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILOT SERIES BANNER */}
      <section className="bg-navy border-t border-slate-soft relative overflow-hidden">
        <div className="absolute inset-0 spotlight-soft pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 relative z-10">
          <div className="corner-bracket p-10 bg-slate-card-2/60 border border-slate-soft">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <div className="eyebrow-bars">Pilot Series</div>
                <h2 className="font-bebas text-white text-4xl sm:text-5xl mt-4 leading-tight">
                  Two Pilot Events.
                  <br />
                  <span className="gold-text">One Standard.</span>
                </h2>
                <p className="font-manrope text-soft-gray mt-4 max-w-xl">
                  Venues, dates, and panel details will be announced as
                  bookings are finalized.
                </p>
              </div>
              <div className="grid gap-4">
                <div className="flex items-baseline gap-3">
                  <span className="font-bebas text-gold text-xl tracking-[0.22em]">
                    Pilot One —
                  </span>
                  <span className="font-shoulders font-black text-white text-2xl">
                    Fall 2026
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-bebas text-gold text-xl tracking-[0.22em]">
                    Pilot Two —
                  </span>
                  <span className="font-shoulders font-black text-white text-2xl">
                    Early 2027
                  </span>
                </div>
                <div className="text-muted-ink text-xs font-bebas tracking-[0.22em] uppercase">
                  {BRAND.city}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND PROMISES */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <SectionLabel number="04">Three Promises</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            What We Will Deliver — <span className="gold-text">In Plain Words.</span>
          </h2>
          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            {PROMISE_CARDS.map((p) => (
              <div
                key={p.tag}
                className="corner-bracket card-dark p-10 flex flex-col"
              >
                <div className="label-bebas text-gold">{p.tag}</div>
                <p className="font-cormorant text-2xl text-white leading-snug mt-4">
                  “{p.body}”
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA TRIO */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <SectionLabel number="05">Three Ways In</SectionLabel>
          <div className="grid md:grid-cols-3 gap-6">
            {CTA_TRIO.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.label}
                  to={c.to}
                  data-testid={c.testId}
                  className="card-dark p-10 flex flex-col group"
                >
                  <Icon className="text-gold" size={36} strokeWidth={1.4} />
                  <h3 className="font-shoulders font-black uppercase tracking-wider text-3xl text-white mt-6">
                    {c.label}
                  </h3>
                  <p className="font-manrope text-soft-gray mt-3">{c.blurb}</p>
                  <div className="mt-8 flex items-center gap-2 text-gold font-bebas tracking-[0.22em] text-sm group-hover:text-gold-bright transition">
                    Continue <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRESENTED BY */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="corner-bracket p-10 bg-slate-card-2/70 border border-slate-soft grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 flex justify-center lg:justify-start">
              <img
                src={ASSETS.producerLogo}
                alt={BRAND.producer}
                data-testid="home-producer-logo"
                className="w-full max-w-[280px] object-contain"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-8">
              <div className="label-bebas text-gold">Produced By</div>
              <h3 className="font-bebas text-white text-3xl sm:text-4xl mt-2 leading-tight">
                {BRAND.producer}
              </h3>
              <p className="font-cormorant text-xl text-soft-gray mt-3">
                {BRAND.founder} — {BRAND.founderTitle}
              </p>
              <p className="font-manrope text-soft-gray mt-4 max-w-xl">
                A for-profit event production and media company based in
                {" "}{BRAND.city}. The Colorado Springs pilot launches a national
                series of The Next Stage events — with future shows planned for
                the Denver Tech Center, New Orleans, Salt Lake City, and
                additional national markets.
              </p>
              <Link
                to="/about"
                data-testid="presented-by-about-link"
                className="btn-outline mt-8"
              >
                <Users size={18} strokeWidth={1.8} />
                About The Producer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-midnight border-t border-slate-soft relative overflow-hidden">
        <div className="absolute inset-0 spotlight-soft pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 relative z-10 text-center">
          <h2 className="font-bebas text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl mx-auto">
            A Powerful Message Deserves More Than A Private Dream.
            <br />
            <span className="gold-text">It Deserves A Stage.</span>
          </h2>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/apply" data-testid="final-cta-apply" className="btn-gold">
              <Mic2 size={18} strokeWidth={1.8} />
              Apply To Speak
              <ArrowRight size={16} />
            </Link>
            <Link to="/sponsor" data-testid="final-cta-sponsor" className="btn-outline">
              <HandCoins size={18} strokeWidth={1.8} />
              Sponsor The Showcase
            </Link>
          </div>
          <p className="font-bebas text-muted-ink tracking-[0.32em] mt-12 text-sm uppercase">
            Are You Ready To Step Forward?
          </p>
        </div>
      </section>
    </div>
  );
}
